import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/nextjs';
import {
  VAIR_PRO_BADGES_API,
  VairProBadgesPayload,
  VairProBadgesResponse,
} from 'constants/payloads/vair/proBadges';
import { useApiGateway } from 'hooks/useApi';
import { useViewer } from 'hooks/useViewer';

/**
 * bounceUserId → isVairPro for the given coaches, via the public badges endpoint.
 * Re-keyed on the viewer as well as the ids so a lookup fired before auth settled
 * is retried once it does, instead of the empty result sticking for the session.
 */
export const useVairProBadges = (userIds: string[]): Record<string, boolean> => {
  const { isSessionLoading, userId: viewerId } = useViewer();
  const [vairProMap, setVairProMap] = useState<VairProBadgesResponse>({});
  const { post: fetchVairProBadges } = useApiGateway<VairProBadgesPayload, VairProBadgesResponse>(
    VAIR_PRO_BADGES_API,
  );

  // Sorted so re-ordering the list (sort dropdown) doesn't refetch the same set.
  const userIdsKey = [...userIds].sort().join(',');
  useEffect(() => {
    if (isSessionLoading) {
      return;
    }
    if (userIds.length === 0) {
      setVairProMap({});
      return;
    }
    fetchVairProBadges({ payload: { userIds } })
      .then((result) => setVairProMap((result.data as VairProBadgesResponse) ?? {}))
      .catch((error) => {
        Sentry.captureException(error, { extra: { userIdCount: userIds.length } });
        setVairProMap({});
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIdsKey, isSessionLoading, viewerId]);

  return vairProMap;
};
